import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store/index.ts";
import { ReporteChequeo } from "../models/ReporteChequeo.ts";
import {
  BranchReportMeta,
  loadBranchReportMetaMap,
  resolveEffectiveBranchMeta,
} from "../utils/payrollBranchMeta.ts";

export function usePayrollBranchMeta(
  records: ReporteChequeo[] | undefined | null,
  fallbackBranchId?: number
): Record<number, BranchReportMeta> {
  const { user } = useSelector((state: RootState) => state.auth);
  const [loaded, setLoaded] = React.useState<Record<number, BranchReportMeta>>(
    {}
  );

  const branchIdsKey = React.useMemo(() => {
    const ids = new Set<number>();
    (records || []).forEach((record) => {
      const branchId = Number(record.branch_id);
      if (Number.isFinite(branchId) && branchId > 0) {
        ids.add(branchId);
      }
    });
    const fallback = Number(fallbackBranchId);
    if (Number.isFinite(fallback) && fallback > 0) {
      ids.add(fallback);
    }
    return [...ids].sort((a, b) => a - b).join(",");
  }, [records, fallbackBranchId]);

  React.useEffect(() => {
    const ids = branchIdsKey
      ? branchIdsKey.split(",").map((id) => Number(id))
      : [];
    if (ids.length === 0) {
      setLoaded({});
      return;
    }
    let cancelled = false;
    loadBranchReportMetaMap(ids, user).then((map) => {
      if (!cancelled) {
        setLoaded(map);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [branchIdsKey, user]);

  return React.useMemo(
    () => resolveEffectiveBranchMeta(records, loaded, fallbackBranchId),
    [records, loaded, fallbackBranchId]
  );
}
